import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    getScopeLabel,
    getReviewStatusLabel,
    getTaskStatusLabel,
    getTaskTypeLabel,
    getRouteStatusLabel,
    getEvidenceStatusLabel,
    getDependencyLabel,
    PMO_METRIC_LABELS,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getPmoTooltip,
    getTaskStatusTone,
    getReviewStatusTone,
    getRouteStatusTone,
    getEvidenceStatusTone,
    getHealthTone,
} from "../utils/pmoStyles";
import { API_BASE } from "../config/orbisConfig";



function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function parseDate(value) {
    if (!value) return null;
    const d = new Date(`${value}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
}

function daysBetween(a, b) {
    if (!a || !b) return 0;
    const ms = 24 * 60 * 60 * 1000;
    return Math.round((b.getTime() - a.getTime()) / ms);
}

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function formatDate(value) {
    if (!value) return "Sin fecha";
    const d = parseDate(value);
    if (!d) return value;

    return d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
    });
}

function formatMonth(date) {
    return date.toLocaleDateString("es-MX", {
        month: "short",
        year: "2-digit",
    });
}

function initials(name = "Usuario") {
    return String(name)
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x[0]?.toUpperCase())
        .join("") || "U";
}

function label(value) {
    return String(value || "sin estado")
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusColor(status, isOverdue) {
    if (isOverdue) return "#DC2626";

    const s = String(status || "").toLowerCase();

    if (s === "terminada") return "#16A34A";
    if (s === "en_proceso") return "#2563EB";
    if (s === "pendiente") return "#64748B";
    if (s === "bloqueada") return "#DC2626";
    if (s === "cancelada") return "#94A3B8";

    return "#7C3AED";
}

function priorityPill(priority) {
    const p = String(priority || "").toLowerCase();

    if (p === "critica") return { bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3" };
    if (p === "alta") return { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
    if (p === "media") return { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" };

    return { bg: "#F8FAFC", color: "#475569", border: "#E2E8F0" };
}

function Pill({ children, tone = "neutral", title }) {
    const styles = PMO_BADGE_TONES;
    const s = styles[tone] || styles.neutral;
    const tooltip = title || getPmoTooltip(children);

    return (
        <span
            title={tooltip}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px 8px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                fontSize: 10,
                fontWeight: 900,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

function UserMini({ name }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
                style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #2563EB, #7C3AED)",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                {initials(name)}
            </div>
            <span
                style={{
                    color: "#334155",
                    fontSize: 11,
                    fontWeight: 850,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {name || "Sin responsable"}
            </span>
        </div>
    );
}

function KpiMini({ label, value, color = "#0F172A" }) {
    return (
        <div
            style={{
                borderRadius: 18,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 12,
                boxShadow: "0 12px 24px rgba(15,23,42,.05)",
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    fontWeight: 950,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                }}
            >
                {label}
            </div>
            <div
                style={{
                    marginTop: 4,
                    fontSize: 22,
                    fontWeight: 950,
                    color,
                    letterSpacing: "-.04em",
                }}
            >
                {value}
            </div>
        </div>
    );
}

function GanttTaskRow({ task, timelineStart, totalDays, onOpenTask }) {
    const start = parseDate(task.start_date || task.due_date);
    const end = parseDate(task.due_date || task.start_date);

    const hasDates = !!start && !!end;
    const startOffset = hasDates ? Math.max(0, daysBetween(timelineStart, start)) : 0;
    const duration = hasDates ? Math.max(1, daysBetween(start, end) + 1) : 1;

    const leftPct = totalDays > 0 ? (startOffset / totalDays) * 100 : 0;
    const widthPct = totalDays > 0 ? Math.max(1.2, (duration / totalDays) * 100) : 4;

    const overdue = safeNumber(task.is_overdue) === 1;
    const color = statusColor(task.status, overdue);
    const progress = Math.max(0, Math.min(100, safeNumber(task.progress_percent)));
    const priority = priorityPill(task.priority);

    return (
        <button
            type="button"
            onClick={() => onOpenTask?.(task)}
            style={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "360px minmax(760px, 1fr)",
                gap: 14,
                alignItems: "center",
                border: 0,
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                padding: "8px 0",
            }}
        >
            <div
                style={{
                    minHeight: 66,
                    borderRadius: 18,
                    background: "#FFFFFF",
                    border: overdue ? "1px solid #FCA5A5" : "1px solid #E2E8F0",
                    padding: 12,
                    boxShadow: overdue
                        ? "0 14px 30px rgba(220,38,38,.08)"
                        : "0 12px 24px rgba(15,23,42,.04)",
                    overflow: "hidden",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: "#0F172A",
                                fontSize: 13,
                                fontWeight: 950,
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {task.wbs_code ? `${task.wbs_code} · ` : ""}
                            {task.task_name}
                        </div>
                        <div
                            style={{
                                marginTop: 4,
                                color: "#64748B",
                                fontSize: 11,
                                fontWeight: 750,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {formatDate(task.start_date)} → {formatDate(task.due_date)}
                        </div>
                    </div>

                    <span
                        title={getPmoTooltip(label(task.priority))}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            height: 24,
                            padding: "0 8px",
                            borderRadius: 999,
                            background: priority.bg,
                            color: priority.color,
                            border: `1px solid ${priority.border}`,
                            fontSize: 10,
                            fontWeight: 950,
                            whiteSpace: "nowrap",
                            cursor: "help",
                        }}
                    >
                        {label(task.priority)}
                    </span>
                </div>

                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {overdue ? (
                        <Pill tone="danger">{safeNumber(task.days_overdue)} días</Pill>
                    ) : (
                        <Pill tone={getTaskStatusTone(task.status)}>{getTaskStatusLabel(task.status)}</Pill>
                    )}

                    {task.depends_on_task_id ? (
                        <Pill tone="purple">
                            Dep. {task.predecessor_wbs_code ? `${task.predecessor_wbs_code} · ` : `#${task.depends_on_task_id} · `}
                            {task.predecessor_name || "Predecesora"} · {getDependencyLabel(task)}
                            {safeNumber(task.lag_days) !== 0
                                ? ` ${safeNumber(task.lag_days) > 0 ? "+" : ""}${safeNumber(task.lag_days)}d`
                                : ""}
                        </Pill>
                    ) : null}

                    <Pill tone="info">{getTaskTypeLabel(task)}</Pill>
                    <Pill tone={getReviewStatusTone(task)}>{getReviewStatusLabel(task)}</Pill>
                    <Pill tone={getRouteStatusTone(task)}>
                        {getRouteStatusLabel(task)}
                    </Pill>
                    <Pill tone={getEvidenceStatusTone(task)}>
                        {getEvidenceStatusLabel(task)}
                    </Pill>
                </div>
            </div>

            <div
                style={{
                    position: "relative",
                    minHeight: 66,
                    borderRadius: 18,
                    background:
                        "repeating-linear-gradient(90deg, #F8FAFC 0, #F8FAFC 79px, #EEF2F7 80px)",
                    border: "1px solid #E2E8F0",
                    overflow: "hidden",
                }}
            >
                {hasDates ? (
                    <div
                        title={`${task.task_name} · ${progress.toFixed(0)}% de avance operativo · ${formatDate(task.start_date)} → ${formatDate(task.due_date)}`}
                        style={{
                            position: "absolute",
                            left: `${leftPct}%`,
                            top: 17,
                            width: `${widthPct}%`,
                            height: 30,
                            borderRadius: 999,
                            background: `${color}33`,
                            border: `1px solid ${color}`,
                            overflow: "hidden",
                            boxShadow: `0 12px 24px ${color}22`,
                        }}
                    >
                        <div
                            style={{
                                width: `${progress}%`,
                                height: "100%",
                                background: color,
                                borderRadius: 999,
                            }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: progress > 55 ? "#FFFFFF" : "#0F172A",
                                fontSize: 10,
                                fontWeight: 950,
                                textShadow: progress > 55 ? "0 1px 8px rgba(0,0,0,.25)" : "none",
                            }}
                        >
                            {progress.toFixed(0)}%
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            position: "absolute",
                            left: 14,
                            top: 18,
                            padding: "7px 10px",
                            borderRadius: 999,
                            background: "#F8FAFC",
                            border: "1px dashed #CBD5E1",
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 850,
                        }}
                    >
                        Sin fechas planeadas
                    </div>
                )}
            </div>
        </button>
    );
}

function GanttProjectBlock({ project, timelineStart, totalDays, onOpenTask }) {
    return (
        <section
            style={{
                borderRadius: 26,
                background: "rgba(255,255,255,.92)",
                border: "1px solid #E2E8F0",
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                padding: 18,
                marginBottom: 18,
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    marginBottom: 14,
                }}
            >
                <div>
                    <h2
                        style={{
                            margin: 0,
                            color: "#0F172A",
                            fontSize: 18,
                            fontWeight: 950,
                            letterSpacing: "-.02em",
                        }}
                    >
                        {project.project_name}
                    </h2>
                    <div
                        style={{
                            marginTop: 4,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 750,
                        }}
                    >
                        {project.project_code || "Sin código"} · {project.tasks?.length || 0} tareas
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <Pill tone={getHealthTone(project.project_health_status)}>
                        {label(project.project_health_status)}
                    </Pill>
                    <Pill tone="info">{label(project.project_status)}</Pill>
                </div>
            </div>

            <div style={{ display: "grid", gap: 6 }}>
                {(project.tasks || []).map((task) => (
                    <GanttTaskRow
                        key={task.task_id}
                        task={task}
                        timelineStart={timelineStart}
                        totalDays={totalDays}
                        onOpenTask={onOpenTask}
                    />
                ))}
            </div>
        </section>
    );
}

function TimelineHeader({ timelineStart, totalDays }) {
    const marks = [];

    if (timelineStart && totalDays > 0) {
        const approxMarks = 8;
        const step = Math.max(7, Math.ceil(totalDays / approxMarks));

        for (let d = 0; d <= totalDays; d += step) {
            marks.push({
                offset: (d / totalDays) * 100,
                date: addDays(timelineStart, d),
            });
        }
    }

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "360px minmax(760px, 1fr)",
                gap: 14,
                alignItems: "center",
                marginBottom: 10,
                position: "sticky",
                top: 0,
                zIndex: 5,
            }}
        >
            <div
                style={{
                    borderRadius: 16,
                    background: "#0F172A",
                    color: "#FFFFFF",
                    padding: "12px 14px",
                    fontSize: 12,
                    fontWeight: 950,
                    boxShadow: "0 12px 24px rgba(15,23,42,.18)",
                }}
            >
                Tareas / WBS
            </div>

            <div
                style={{
                    height: 44,
                    borderRadius: 16,
                    background: "#0F172A",
                    color: "#FFFFFF",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 12px 24px rgba(15,23,42,.18)",
                }}
            >
                {marks.map((mark, idx) => (
                    <div
                        key={idx}
                        style={{
                            position: "absolute",
                            left: `${mark.offset}%`,
                            top: 0,
                            height: "100%",
                            transform: "translateX(-50%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#CBD5E1",
                            fontSize: 11,
                            fontWeight: 850,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {formatMonth(mark.date)}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function GanttPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(() => {
        return searchParams.get("project_id") || "all";
    });

    const loadGantt = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("cani_auth") || "";
            const qp = new URLSearchParams();

            if (selectedProjectId !== "all") {
                qp.set("project_id", selectedProjectId);
            }

            const response = await fetch(`${API_BASE}/pmo_gantt.php${qp.toString() ? `?${qp.toString()}` : ""}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const txt = await response.text();

            if (!txt || !txt.trim()) {
                throw new Error("Respuesta vacía del Gantt PMO");
            }

            const json = JSON.parse(txt);

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo cargar Gantt PMO");
            }

            setData(json.data || null);
        } catch (e) {
            setError(e.message || "Error al cargar Gantt PMO");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGantt();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId]);

    const projects = data?.projects || [];
    const projectFilterOptions = data?.project_options || projects;

    const allProjectOptions = useMemo(() => {
        return projectFilterOptions.map((p) => ({
            id: p.project_id,
            name: p.project_name,
        }));
    }, [projectFilterOptions]);

    const timelineStart = useMemo(() => {
        if (!data?.summary?.timeline_start) return null;
        return parseDate(data.summary.timeline_start);
    }, [data]);

    const timelineEnd = useMemo(() => {
        if (!data?.summary?.timeline_end) return null;
        return parseDate(data.summary.timeline_end);
    }, [data]);

    const totalDays = useMemo(() => {
        if (!timelineStart || !timelineEnd) return 0;
        return Math.max(1, daysBetween(timelineStart, timelineEnd) + 1);
    }, [timelineStart, timelineEnd]);

    const openTask = (task) => {
        if (!task?.task_id) return;

        const returnParams = new URLSearchParams();

        if (selectedProjectId && selectedProjectId !== "all") {
            returnParams.set("project_id", selectedProjectId);
        }

        const returnTo = `/gantt${returnParams.toString() ? `?${returnParams.toString()}` : ""}`;

        const qp = new URLSearchParams();
        qp.set("mode", "progress");
        qp.set("return_to", returnTo);
        qp.set("return_label", "Gantt PMO");

        navigate(`/my-work/task/${task.task_id}?${qp.toString()}`);
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                Cargando Gantt PMO...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    borderRadius: 20,
                    background: "#FFF1F2",
                    border: "1px solid #FECDD3",
                    color: "#BE123C",
                    padding: 20,
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                {error}
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 24,
                    background:
                        "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 20px 50px rgba(15,23,42,.07)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 20,
                }}
            >
                <div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            color: "#1D4ED8",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                            marginBottom: 14,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: "#2563EB",
                            }}
                        />
                        Planeación visual
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 32,
                            lineHeight: 1.05,
                            color: "#0F172A",
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                        }}
                    >
                        Gantt PMO
                    </h1>

                    <p
                        style={{
                            margin: "10px 0 0",
                            color: "#64748B",
                            fontSize: 14,
                            lineHeight: 1.5,
                            maxWidth: 820,
                            fontWeight: 700,
                        }}
                    >
                        Línea de tiempo visual por proyecto, WBS, avance, vencimientos,
                        responsables, dependencias y salud operativa.
                    </p>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(90px, 1fr))",
                        gap: 10,
                        minWidth: 350,
                    }}
                >
                    <KpiMini label="Proyectos" value={data?.summary?.total_projects || 0} color="#0F172A" />
                    <KpiMini label="Tareas" value={data?.summary?.total_tasks || 0} color="#2563EB" />
                    <KpiMini label="Días línea" value={totalDays} color="#7C3AED" />
                </div>
            </section>

            <section
                style={{
                    marginBottom: 18,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <select
                    value={selectedProjectId}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSelectedProjectId(value);

                        const next = new URLSearchParams(searchParams);

                        if (!value || value === "all") {
                            next.delete("project_id");
                        } else {
                            next.set("project_id", value);
                        }

                        setSearchParams(next);
                    }}
                    style={{
                        height: 42,
                        minWidth: 320,
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 800,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                >
                    <option value="all">Portafolio PMO</option>
                    {allProjectOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>

                <button
                    type="button"
                    onClick={loadGantt}
                    style={{
                        height: 42,
                        padding: "0 16px",
                        borderRadius: 999,
                        border: "1px solid #0F172A",
                        background: "#0F172A",
                        color: "#FFFFFF",
                        fontSize: 12,
                        fontWeight: 950,
                        cursor: "pointer",
                        boxShadow: "0 14px 28px rgba(15,23,42,.18)",
                    }}
                >
                    Actualizar
                </button>
            </section>

            <div style={{ overflowX: "auto", paddingBottom: 16 }}>
                <TimelineHeader timelineStart={timelineStart} totalDays={totalDays} />

                {projects.length ? (
                    projects.map((project) => (
                        <GanttProjectBlock
                            key={project.project_id}
                            project={project}
                            timelineStart={timelineStart}
                            totalDays={totalDays}
                            onOpenTask={openTask}
                        />
                    ))
                ) : (
                    <div
                        style={{
                            border: "1px dashed #CBD5E1",
                            borderRadius: 18,
                            padding: 18,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 800,
                            textAlign: "center",
                            background: "rgba(255,255,255,.62)",
                        }}
                    >
                        No hay tareas con información suficiente para construir el Gantt.
                    </div>
                )}
            </div>
        </div>
    );
}
